import { useCallback, useEffect, useRef, useState } from 'react';
import { trackAPI } from '../services/api';
import { BackgroundLocation } from '../services/LocationService';
import { Capacitor } from '@capacitor/core';
import { LocationService } from '../services/LocationService';
import LocationConsentService from '../services/LocationConsentService';

// Simple local storage queue (can be upgraded to IndexedDB later)
const QUEUE_KEY = 'okio_track_queue_v1';
const TRAIL_STORAGE_KEY = 'okio_session_trails_v1';
const MAX_TRAIL_POINTS = 5000;
const MAX_SESSIONS_PER_PROJECT = 12;

const loadQueue = () => {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; }
};
const saveQueue = (q) => {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch {}
};

const loadTrailStore = () => {
  try { return JSON.parse(localStorage.getItem(TRAIL_STORAGE_KEY) || '{}'); } catch { return {}; }
};

const saveTrailStore = (store) => {
  try { localStorage.setItem(TRAIL_STORAGE_KEY, JSON.stringify(store)); } catch {}
};

const getStoredTrail = (projectId, sessionId) => {
  if (!projectId || !sessionId) return [];
  const store = loadTrailStore();
  return store?.[projectId]?.[sessionId]?.points || [];
};

const persistTrail = (projectId, sessionId, points) => {
  if (!projectId || !sessionId) return;
  const cappedPoints = Array.isArray(points)
    ? (points.length > MAX_TRAIL_POINTS ? points.slice(points.length - MAX_TRAIL_POINTS) : points)
    : [];
  const store = loadTrailStore();
  const projectStore = store[projectId] || {};
  projectStore[sessionId] = {
    points: cappedPoints,
    updatedAt: Date.now()
  };
  const entries = Object.entries(projectStore);
  if (entries.length > MAX_SESSIONS_PER_PROJECT) {
    entries
      .sort((a, b) => (a[1]?.updatedAt || 0) - (b[1]?.updatedAt || 0))
      .slice(0, entries.length - MAX_SESSIONS_PER_PROJECT)
      .forEach(([oldSessionId]) => {
        delete projectStore[oldSessionId];
      });
  }
  store[projectId] = projectStore;
  saveTrailStore(store);
};

const computeDistanceMeters = (a, b) => {
  if (!a || !b) return Infinity;
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const aVal = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal));
  return R * c;
};

const isNativePlatform = (() => {
  try { return Capacitor?.isNativePlatform?.() === true; } catch { return false; }
})();

const claimLiveTrackingOwner = () => {
  try {
    const current = Number(window.__okio_live_track_claims__ || 0);
    window.__okio_live_track_claims__ = current + 1;
  } catch {}
};

const releaseLiveTrackingOwner = () => {
  try {
    const current = Number(window.__okio_live_track_claims__ || 0);
    window.__okio_live_track_claims__ = Math.max(0, current - 1);
  } catch {}
};

export const useLiveTrack = ({ projectId, sessionId, enabled, config = {} }) => {
  const [trackId, setTrackId] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [points, setPoints] = useState([]);
  const [heading, setHeading] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const watchIdRef = useRef(null);
  const bufferRef = useRef([]);
  const lastFixRef = useRef(null);
  const lastAcceptTimeRef = useRef(0);
  const lastSentAtRef = useRef(0);
  const isSendingRef = useRef(false);
  const firstFixAcceptedRef = useRef(false);
  const sessionIdRef = useRef(null);
  const pointsRef = useRef([]);
  const persistTimeoutRef = useRef(null);

  // On web, no disclosure is needed — the browser handles permission prompts.
  // On native, start conservatively as false and async-check the real permission
  // state. This ensures the disclosure always precedes the OS runtime prompt,
  // even after an "Only this time" grant expires between app launches.
  const [locationConsented, setLocationConsented] = useState(!isNativePlatform);

  useEffect(() => {
    if (!isNativePlatform) return;
    // If the Android permission is already permanently granted, we can skip
    // the disclosure — no OS prompt will appear.  If the permission needs
    // (re-)requesting (e.g. "Only this time" expired), invalidate any stored
    // consent so the disclosure shows before the next OS prompt.
    LocationService.checkPermissions().then(status => {
      if (status === 'granted' || status === 'limited') {
        setLocationConsented(true);
      } else {
        LocationConsentService.reset();
      }
    }).catch(() => {});
    const handler = () => setLocationConsented(true);
    window.addEventListener('okio:bg_location_consent_granted', handler);
    return () => window.removeEventListener('okio:bg_location_consent_granted', handler);
  }, []);

  const intervalSeconds = config.intervalSeconds ?? 3;
  const minDistanceMeters = config.minDistanceMeters ?? 7;
  const maxAccuracyMeters = config.maxAccuracyMeters ?? 100;
  const batchMaxPoints = config.batchMaxPoints ?? 20;
  const batchMaxDelayMs = config.batchMaxDelayMs ?? 15000;

  const persistPoints = useCallback((pts, immediate = false) => {
    if (!projectId || !sessionId) return;
    const commit = () => persistTrail(projectId, sessionId, pts);
    if (immediate) {
      commit();
      return;
    }
    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current);
    }
    persistTimeoutRef.current = setTimeout(commit, 500);
  }, [projectId, sessionId]);

  const startBackgroundWatcher = useCallback(async () => {
    try {
      if (!Capacitor?.isNativePlatform?.()) return;
      if (!LocationConsentService.hasAccepted()) return;
      const permOk = await LocationService.requestPermissions();
      if (!permOk) return;
      const id = await BackgroundLocation.start({ distanceFilter: Math.max(minDistanceMeters, 25) });
      window.__okio_bg_watcher_id__ = id;
    } catch {
      // Non-fatal — foreground tracking still works
    }
  }, [minDistanceMeters]);

  const start = useCallback(async () => {
    if (!projectId || !sessionId || isTracking) return;

    // On native, the prominent disclosure MUST be shown and accepted before
    // any location permission is requested (Google Play policy). If consent
    // hasn't been given yet, show the disclosure and bail — everything will
    // re-trigger once the user accepts via the locationConsented state change.
    if (isNativePlatform && !locationConsented) {
      if (!LocationConsentService.wasPromptedThisSession()) {
        window.dispatchEvent(new CustomEvent('okio:request_location_disclosure'));
      }
      return;
    }

    try {
      // First try to reuse existing active track
      try {
        const cur = await trackAPI.getCurrent(projectId, { chatId: sessionId });
        const existingId = cur?.data?.id || cur?.data?.trackId;
        if (existingId) {
          setTrackId(existingId);
          setIsTracking(true);
          startBackgroundWatcher();
          return;
        }
      } catch {}

      // Otherwise start a new track
      const { data } = await trackAPI.start(projectId, {
        chatId: sessionId,
        intervalSeconds, minDistanceMeters, maxAccuracyMeters,
        deviceInfo: { ua: navigator.userAgent }
      });
      setTrackId(data.trackId || data?.track?.id);
      setIsTracking(true);
      startBackgroundWatcher();
    } catch (e) {
      // If conflict, fetch current and continue
      if (e?.response?.status === 409) {
        try {
          const cur = await trackAPI.getCurrent(projectId, { chatId: sessionId });
          const existingId = cur?.data?.id || cur?.data?.trackId;
          if (existingId) {
            setTrackId(existingId);
            setIsTracking(true);
            startBackgroundWatcher();
            return;
          }
        } catch {}
      }
      console.error('Failed to start track', e);
    }
  }, [projectId, sessionId, isTracking, locationConsented, intervalSeconds, minDistanceMeters, maxAccuracyMeters, startBackgroundWatcher]);

  const enqueue = useCallback((items) => {
    const q = loadQueue();
    q.push({ projectId, trackId, items, ts: Date.now() });
    saveQueue(q);
  }, [projectId, trackId]);

  const flushQueue = useCallback(async () => {
    if (!projectId || !trackId) return;
    const q = loadQueue();
    const remain = [];
    for (const entry of q) {
      if (entry.projectId !== projectId || entry.trackId !== trackId) { remain.push(entry); continue; }
      try {
        await trackAPI.ingestPoints(projectId, trackId, { points: entry.items, maxAccuracyMeters });
      } catch (e) {
        remain.push(entry);
      }
    }
    saveQueue(remain);
  }, [projectId, trackId, maxAccuracyMeters]);

  const flush = useCallback(async () => {
    if (!projectId || !trackId) return;
    if (isSendingRef.current) return;
    isSendingRef.current = true;
    let bufferToSend = null;
    try {
      const currentBuffer = bufferRef.current;
      if (currentBuffer.length) {
        bufferToSend = currentBuffer;
        bufferRef.current = [];
        await trackAPI.ingestPoints(projectId, trackId, { points: bufferToSend, maxAccuracyMeters });
        lastSentAtRef.current = Date.now();
      }
      await flushQueue();
    } catch (e) {
      // Requeue the buffer we attempted to send (not the now-cleared ref)
      try {
        if (Array.isArray(bufferToSend) && bufferToSend.length) enqueue(bufferToSend);
      } catch {}
      console.warn('Track flush failed; queued', e);
    } finally {
      isSendingRef.current = false;
    }
  }, [projectId, trackId, flushQueue, maxAccuracyMeters, enqueue]);

  const stop = useCallback(async (finish = true) => {
    try {
      // Stop background watcher if running (native only)
      try {
        if (Capacitor?.isNativePlatform?.()) {
          await BackgroundLocation.stop(window.__okio_bg_watcher_id__);
          window.__okio_bg_watcher_id__ = null;
        }
      } catch {}
      // Clear foreground watcher for both native (wrapped) and web
      if (watchIdRef.current) {
        try {
          if (typeof watchIdRef.current.clear === 'function') {
            await watchIdRef.current.clear();
          } else if (navigator.geolocation) {
            navigator.geolocation.clearWatch(watchIdRef.current);
          }
        } catch {}
        watchIdRef.current = null;
      }
      if (finish && trackId) {
        await flush();
        await trackAPI.finish(projectId, trackId);
      }
    } finally {
      setIsTracking(false);
    }
  }, [projectId, trackId, flush]);

  const scheduleFlush = useCallback(() => {
    if (Date.now() - lastSentAtRef.current > batchMaxDelayMs || bufferRef.current.length >= batchMaxPoints) {
      flush();
    }
  }, [flush, batchMaxDelayMs, batchMaxPoints]);

  // Persist points per session for later playback
  useEffect(() => {
    pointsRef.current = points;
    persistPoints(points);
  }, [points, persistPoints]);

  useEffect(() => {
    let cancelled = false;
    const handleSessionChange = async () => {
      const previousSessionId = sessionIdRef.current;
      if (previousSessionId && previousSessionId !== sessionId) {
        try {
          persistTrail(projectId, previousSessionId, pointsRef.current);
        } catch {}
        try {
          await stop(true);
        } catch {}
        bufferRef.current = [];
        lastFixRef.current = null;
        lastAcceptTimeRef.current = 0;
        firstFixAcceptedRef.current = false;
        setTrackId(null);
      }

      if (!projectId || !sessionId) {
        sessionIdRef.current = sessionId || null;
        pointsRef.current = [];
        if (!cancelled) {
          setPoints([]);
        }
        setIsTracking(false);
        return;
      }

      sessionIdRef.current = sessionId;
      const cachedPoints = getStoredTrail(projectId, sessionId) || [];
      pointsRef.current = cachedPoints;
      if (!cancelled) {
        setPoints(cachedPoints);
      }
    };

    handleSessionChange();

    return () => {
      cancelled = true;
    };
  }, [projectId, sessionId, stop]);

  useEffect(() => {
    return () => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }
      if (sessionIdRef.current && pointsRef.current.length) {
        persistTrail(projectId, sessionIdRef.current, pointsRef.current);
      }
    };
    // projectId is stable per hook usage, so include for completeness
  }, [projectId]);

  // Ensure track is started once when enabled
  useEffect(() => {
    if (!enabled || !projectId || !sessionId) return;
    start();
  }, [enabled, projectId, sessionId, start]);

  useEffect(() => {
    if (!enabled) return undefined;
    claimLiveTrackingOwner();
    return () => releaseLiveTrackingOwner();
  }, [enabled]);

  // Stop tracking when disabled to avoid leaking watchers
  useEffect(() => {
    if (enabled) return;
    (async () => {
      try {
        await stop(false);
      } catch {}
    })();
  }, [enabled, stop]);

  // Start geolocation watch (single registration).
  // On native this is gated on locationConsented so that the OS runtime
  // permission prompt only fires AFTER the prominent disclosure is accepted.
  useEffect(() => {
    if (!enabled || !projectId || !sessionId || !locationConsented) return;
    if (watchIdRef.current) return; // already watching
    let cancelled = false;
    firstFixAcceptedRef.current = false;
    try {
      const subPromise = LocationService.watchPosition(
        (pos) => {
          if (cancelled) return;
          const { latitude, longitude, accuracy: acc, altitude, speed, heading } = pos.coords;
          const now = new Date().toISOString();
          const point = { ts: now, lat: latitude, lng: longitude, accuracy: acc, altitude, speed, heading, source: 'geolocation' };
          // Accept the first reasonable fix even if a bit coarse, then enforce configured threshold
          if (!firstFixAcceptedRef.current) {
            if (acc != null && acc > Math.max(250, maxAccuracyMeters)) return;
            firstFixAcceptedRef.current = true;
          } else {
            if (acc != null && acc > maxAccuracyMeters) return;
          }
          // Distance/time check
          const last = lastFixRef.current;
          const distanceOk = !last || computeDistanceMeters(last, { lat: latitude, lng: longitude }) >= minDistanceMeters;
          const nowMs = Date.now();
          const timeOk = nowMs - (lastAcceptTimeRef.current || 0) >= intervalSeconds * 1000;
          if (!distanceOk && !timeOk) return;
          lastFixRef.current = { lat: latitude, lng: longitude };
          lastAcceptTimeRef.current = nowMs;
          setHeading(typeof heading === 'number' ? heading : null);
          setAccuracy(acc ?? null);
          setPoints((prev) => {
            const next = [...prev, [latitude, longitude]];
            return next.length > MAX_TRAIL_POINTS ? next.slice(next.length - MAX_TRAIL_POINTS) : next;
          });
          bufferRef.current.push(point);
          scheduleFlush();
        },
        (err) => {
          console.warn('LiveTrack geolocation error', err?.code, err?.message);
        },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
      );
      subPromise.then((sub) => { watchIdRef.current = sub; }).catch(() => {});
    } catch (e) {
      console.warn('Geolocation watch setup failed', e);
    }
    return () => {
      cancelled = true;
      if (watchIdRef.current && watchIdRef.current.clear) {
        try { watchIdRef.current.clear(); } catch {}
        watchIdRef.current = null;
      }
    };
  }, [enabled, projectId, sessionId, locationConsented, minDistanceMeters, maxAccuracyMeters, intervalSeconds, scheduleFlush]);

  // Periodic flush
  useEffect(() => {
    if (!enabled || !projectId || !sessionId) return;
    const id = setInterval(() => scheduleFlush(), 5000);
    return () => clearInterval(id);
  }, [enabled, projectId, sessionId, scheduleFlush]);

  // Native-only: ingest background location events into the buffer
  useEffect(() => {
    if (!enabled || !projectId || !sessionId) return;
    const isNative = (() => { try { return Capacitor?.isNativePlatform?.() === true; } catch { return false; } })();
    if (!isNative) return;

    const handler = (ev) => {
      try {
        const loc = ev?.detail;
        const coords = loc?.coords || loc;
        if (!coords) return;
        const { latitude, longitude, accuracy: acc, altitude, speed, heading } = coords;
        if (acc != null && acc > maxAccuracyMeters) return;
        const nowIso = new Date().toISOString();
        const point = { ts: nowIso, lat: latitude, lng: longitude, accuracy: acc, altitude, speed, heading, source: 'bg' };
        const last = lastFixRef.current;
        const distanceOk = !last || computeDistanceMeters(last, { lat: latitude, lng: longitude }) >= minDistanceMeters;
        const nowMs = Date.now();
        const timeOk = nowMs - (lastAcceptTimeRef.current || 0) >= intervalSeconds * 1000;
        if (!distanceOk && !timeOk) return;
        lastFixRef.current = { lat: latitude, lng: longitude };
        lastAcceptTimeRef.current = nowMs;
        bufferRef.current.push(point);
        scheduleFlush();
      } catch {}
    };

    window.addEventListener('okio:bg_location', handler);
    return () => {
      window.removeEventListener('okio:bg_location', handler);
    };
  }, [enabled, projectId, sessionId, minDistanceMeters, maxAccuracyMeters, intervalSeconds, scheduleFlush]);

  useEffect(() => {
    return () => {
      releaseLiveTrackingOwner();
      try {
        BackgroundLocation.stop(window.__okio_bg_watcher_id__);
      } catch {}
    };
  }, []);

  // Optionally finalize track when disabled if explicitly requested
  const finalizeOnDisable = config.finalizeOnDisable === true;
  useEffect(() => {
    if (!finalizeOnDisable) return;
    if (!enabled && trackId) {
      (async () => {
        try {
          await flush();
          await trackAPI.finish(projectId, trackId);
        } catch {}
      })();
    }
  }, [enabled, trackId, projectId, flush, finalizeOnDisable]);

  return {
    trackId,
    isTracking,
    points,
    heading,
    accuracy,
    start,
    stop,
    flush
  };
};

export const getStoredSessionTrail = (projectId, sessionId) => getStoredTrail(projectId, sessionId);

export default useLiveTrack;
